import { ReactNode, useState } from "react";
import { FilterActions } from "../../UI/Table/Filters/FilterActions.tsx";
import {
  DEFAULT_SALES_PERIOD_PRESET,
  SalesComparison,
  SalesPeriod,
  SalesPeriodPreset,
} from "./salesPeriod.ts";
import { formatPeriod } from "./salesFormat.ts";
import { FilterPopoverField } from "./FilterPopoverField.tsx";

const CALENDAR_DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_COMPARISON = SalesComparison.PreviousPeriod;

const PERIOD_PRESET_LABELS: Record<SalesPeriodPreset, string> = {
  [SalesPeriodPreset.ThisMonth]: "Mese in corso",
  [SalesPeriodPreset.Last7Days]: "Ultimi 7 giorni",
  [SalesPeriodPreset.Last30Days]: "Ultimi 30 giorni",
  [SalesPeriodPreset.Last90Days]: "Ultimi 90 giorni",
  [SalesPeriodPreset.Last12Months]: "Ultimi 12 mesi",
  [SalesPeriodPreset.ThisYear]: "Anno in corso",
  [SalesPeriodPreset.Custom]: "Personalizzato",
};

const COMPARISON_LABELS: Record<SalesComparison, string> = {
  [SalesComparison.PreviousPeriod]: "Periodo precedente",
  [SalesComparison.LastYear]: "Anno scorso",
};

const dateInputClassName =
  "w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700";

export type PeriodChoice = {
  preset: SalesPeriodPreset;
  customFrom?: string;
  customTo?: string;
  comparison: SalesComparison;
};

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="px-1 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
      {children}
    </p>
  );
}

function RadioOption({
  name,
  label,
  checked,
  onSelect,
}: {
  name: string;
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm text-gray-700 hover:bg-gray-50">
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        className="w-4 h-4 shrink-0 accent-primary"
      />
      {label}
    </label>
  );
}

function PeriodPicker({
  preset,
  period,
  comparison,
  onApply,
}: {
  preset: SalesPeriodPreset;
  period: SalesPeriod;
  comparison: SalesComparison;
  onApply: (choice: PeriodChoice) => void;
}) {
  const [draftPreset, setDraftPreset] = useState(preset);
  const [draftComparison, setDraftComparison] = useState(comparison);
  const [customFrom, setCustomFrom] = useState(
    period.from.format(CALENDAR_DATE_FORMAT),
  );
  const [customTo, setCustomTo] = useState(
    period.to.format(CALENDAR_DATE_FORMAT),
  );

  return (
    <div className="flex flex-col gap-1 px-2">
      <SectionLabel>Periodo</SectionLabel>
      {Object.values(SalesPeriodPreset).map((option) => (
        <RadioOption
          key={option}
          name="sales-period"
          label={PERIOD_PRESET_LABELS[option]}
          checked={draftPreset === option}
          onSelect={() => setDraftPreset(option)}
        />
      ))}
      {draftPreset === SalesPeriodPreset.Custom && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          <label className="flex flex-col gap-1 text-xs text-gray-500">
            Dal
            <input
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(event) => setCustomFrom(event.target.value)}
              className={dateInputClassName}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-500">
            Al
            <input
              type="date"
              value={customTo}
              min={customFrom}
              onChange={(event) => setCustomTo(event.target.value)}
              className={dateInputClassName}
            />
          </label>
        </div>
      )}
      <hr className="my-1 border-gray-100" />
      <SectionLabel>Confronta con</SectionLabel>
      {Object.values(SalesComparison).map((option) => (
        <RadioOption
          key={option}
          name="sales-comparison"
          label={COMPARISON_LABELS[option]}
          checked={draftComparison === option}
          onSelect={() => setDraftComparison(option)}
        />
      ))}
      <FilterActions
        canClear={
          preset !== DEFAULT_SALES_PERIOD_PRESET ||
          comparison !== DEFAULT_COMPARISON
        }
        onClear={() =>
          onApply({
            preset: DEFAULT_SALES_PERIOD_PRESET,
            comparison: DEFAULT_COMPARISON,
          })
        }
        onApply={() =>
          onApply(
            draftPreset === SalesPeriodPreset.Custom
              ? {
                  preset: draftPreset,
                  customFrom,
                  customTo,
                  comparison: draftComparison,
                }
              : { preset: draftPreset, comparison: draftComparison },
          )
        }
      />
    </div>
  );
}

function summaryOf({
  preset,
  period,
  comparison,
}: {
  preset: SalesPeriodPreset;
  period: SalesPeriod;
  comparison: SalesComparison;
}): string {
  const periodLabel =
    preset === SalesPeriodPreset.Custom
      ? formatPeriod(period)
      : PERIOD_PRESET_LABELS[preset];
  return comparison === DEFAULT_COMPARISON
    ? periodLabel
    : `${periodLabel} · vs ${COMPARISON_LABELS[comparison].toLowerCase()}`;
}

export function PeriodFilterField({
  preset,
  period,
  comparison,
  onChange,
}: {
  preset: SalesPeriodPreset;
  period: SalesPeriod;
  comparison: SalesComparison;
  onChange: (choice: PeriodChoice) => void;
}) {
  return (
    <FilterPopoverField
      summary={summaryOf({ preset, period, comparison })}
      isEmpty={false}
    >
      {(close) => (
        <PeriodPicker
          preset={preset}
          period={period}
          comparison={comparison}
          onApply={(choice) => {
            onChange(choice);
            close();
          }}
        />
      )}
    </FilterPopoverField>
  );
}
